import {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import ArtistDescriptionModal from "../../components/ArtistDescriptionModal";
import LinkPaymentModal from "../../components/LinkPaymentModal";
import UpdatePasswordModal from "../../components/UpdatePasswordModal";
import { usePersistStore, useStore } from "../../store";
import Avatar from "../../components/Avatar";
import Confirm from "../../components/Confirm";
import { BlobService, UserService } from "../../services";
import { useMyVerificationRequest } from "../../hooks/queries";
import {
  PhoneText,
  Text,
  Phone,
  Zip,
  DoB,
  Timezone,
} from "../../controls";
import Utils from "../../utils/utils";
import { PutBlobResult, put } from "@vercel/blob";
import { v4 as uuid } from "uuid";
import VerifyProfilePopup from "../../components/VerifyProfilePopup";
import CommonModal from "../../components/CommonModal";
import {
  DASHBOARD_TYPE,
  VERIFICATION_REQUEST_STATUS,
} from "../../utils/enums";
import StatesDropdown from "../../components/StatesDropdown";
import CitiesDropdown from "../../components/CitiesDropdown";
import SpecialityDropdown from "../../components/SpecialityDropdown";
import { Skeleton } from "boneyard-js/react";
import UserBadge from "../../components/UserBadge";
import { VerificationStatusChip } from "../../components/status";

interface DetailRowProps {
  label: string;
  value?: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="previewRow">
    <span className="previewRow__label">{label}</span>
    <span className="previewRow__value">{value || "-"}</span>
  </div>
);

function Profile() {
  const [showDescriptionModel, setShowDescriptionModel] = useState(false);
  const [showPaymentModel, setShowPaymentModel] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showEditMode, setshowEditMode] = useState(false);
  const [showVerifyProfile, setShowVerifyProfile] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState<boolean>(false);
  const [showActionsMenu, setShowActionsMenu] = useState<boolean>(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const {
    token,
    user,
    setUser,
    dashboardType,
    isVerifiedUser,
    setIsBuyerProfileCompleted,
  } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage, setIsLoading } = useStore(
    (s: any) => s
  );

  const [values, setValues] = useState({
    name: user?.name,
    dob: user?.dob,
    phone: user?.phone,
    address1: user?.address1,
    address2: user?.address2,
    city: user?.city,
    state: user?.state,
    zip: user?.zip,
    speciality: user?.speciality,
    timezone: user?.timezone,
  });
  const [validationMessage, setValidationMessage] = useState<string>("");
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [image, setImage] = useState<string>(user?.fileUrl);
  // Only fetched for unverified users (the hook is disabled otherwise).
  const { data: verificationStatus, refetch: refetchVerification } =
    useMyVerificationRequest(token, !isVerifiedUser);

  // Refetch the current user on mount so the persist store picks up
  // any fields that were added server-side after the user logged in
  // (e.g. the `code` reference number that was backfilled into existing
  // accounts). Without this, an already-logged-in user would have to
  // sign out and back in for new fields to appear.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    UserService.reload(token).then((result: any) => {
      if (cancelled) return;
      if (result?.data) setUser(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, [token, setUser]);

  // Close the "more actions" overflow menu on an outside click or Escape.
  useEffect(() => {
    if (!showActionsMenu) return;
    const onPointerDown = (e: MouseEvent) => {
      if (
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(e.target as Node)
      ) {
        setShowActionsMenu(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowActionsMenu(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showActionsMenu]);

  const handleChangeFile = useCallback(
    async (event: any) => {
      const file = event.target.files[0];
      if (!file) return;

      setImage(URL.createObjectURL(file));
      setIsLoading(true);
      const ext = Utils.getFileExtension(file?.name);
      const filename = `${uuid()}.${ext}`;
      const tokenResult = await BlobService.getClientToken(token, { filename });
      if (tokenResult.error) {
        setErrorMessage(tokenResult.error);
        setIsLoading(false);
        return;
      }

      const clientToken = tokenResult?.data?.token;
      const blob: PutBlobResult = await put(filename, file, {
        access: "public",
        token: clientToken,
      });
      if (!blob) {
        setIsLoading(false);
        setErrorMessage("Failed to upload image. Please try again.");
        return;
      }

      let result = await UserService.updatePicture(token, {
        url: blob?.url,
      });
      if (result.error) {
        setErrorMessage(result.error);
        setIsLoading(false);
        return;
      }

      setUser(result?.data);
      setIsLoading(false);
      setSuccessMessage("Profile picture updated successfully.");
    },
    [token, setErrorMessage, setIsLoading, setSuccessMessage, setUser]
  );

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setValues({
        ...values,
        [name]: value,
      });
    },
    [values, setValues]
  );

  const handleRemovePicture = useCallback(
    async (e: any) => {
      e?.preventDefault();
      let result = await UserService.removePicture(token);
      if (result?.error) {
        setErrorMessage(result?.error);
        return;
      }

      setImage("");
      setSuccessMessage("Profile picture removed.");
      setUser(result?.data);
      setShowConfirm(false);
    },
    [token, setErrorMessage, setSuccessMessage, setUser]
  );

  const handleUpdate = useCallback(
    async (e: any) => {
      e?.preventDefault();
      if (!values?.name)
        return setValidationMessage("Please provide your name.");
      if (!values?.phone)
        return setValidationMessage("Please provide your phone number.");
      if (!values?.dob)
        return setValidationMessage("Please provide your date of birth.");
      if (!values?.address1)
        return setValidationMessage("Please provide your address line 1.");
      if (!values?.address2)
        return setValidationMessage("Please provide your address line 2.");
      if (!values?.city)
        return setValidationMessage("Please provide your city.");
      if (!values?.state)
        return setValidationMessage("Please provide your state.");
      if (!values?.zip) return setValidationMessage("Please provide your zip.");
      if (!values?.speciality)
        return setValidationMessage("Please provide your speciality.");
      if (!values?.timezone)
        return setValidationMessage("Please select your default timezone.");

      let result = await UserService.update(token, values);
      if (result?.error) {
        setErrorMessage(result?.error);
        return;
      }

      setSuccessMessage("Profile updated successfully.");
      setUser(result?.data);
      setIsBuyerProfileCompleted(true);
      setshowEditMode(false);
      setValidationMessage("");
      if (dashboardType === DASHBOARD_TYPE.SELLER) {
        setShowPaymentModel(true);
      }
    },
    [
      token,
      values,
      setErrorMessage,
      setSuccessMessage,
      setUser,
      dashboardType,
      setIsBuyerProfileCompleted,
    ]
  );

  const isProfileComplete =
    user?.dob &&
    user?.phone &&
    user?.address1 &&
    user?.address2 &&
    user?.city &&
    user?.state &&
    user?.zip &&
    user?.speciality;

  return (
    <>
      <div className="bg-pink p-4">
        <div className="container-fluid px-0">
          <div className="row">
            <div className="col-12 col-xl-9 mx-auto">
              {/* Page header */}
              <div className="tableCardHeader px-0 mb-4">
                <div className="tableCardHeader__titleBlock">
                  <h2 className="tableCardHeader__title m-0">My Profile</h2>
                  <p className="tableCardHeader__subtitle m-0">
                    Identity, address, and preferences buyers see on your
                    listings.
                  </p>
                </div>
              </div>

              {/* Identity card — avatar + name + actions */}
              <div className="profileHero">
                <div className="profileHero__left">
                  <div className="profileHero__avatarWrap">
                    <Avatar
                      src={image}
                      name={user?.name}
                      className="profileHero__avatar"
                    />
                    <button
                      type="button"
                      className="profileHero__avatarEdit"
                      aria-label="Change profile picture"
                      onClick={() => inputFile.current?.click()}
                    >
                      <i className="bi bi-camera" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="profileHero__identity">
                    <h3 className="profileHero__name">{user?.name || "—"}</h3>
                    {user?.email && (
                      <p className="profileHero__email">{user.email}</p>
                    )}
                    {user?.badges && user?.badges?.length > 0 && (
                      <div className="profileHero__badges">
                        {user?.badges?.map((b: string) => (
                          <UserBadge key={b} badge={b} size="small" />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="profileHero__actions">
                  {user?._id && (
                    <Link
                      to={`/seller/${user._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="profileHero__primaryBtn"
                    >
                      <i className="bi bi-eye" aria-hidden="true" />
                      <span>View public profile</span>
                    </Link>
                  )}
                  <input
                    id="txt-user-profile-picture"
                    type="file"
                    accept="image/png, image/jpg, image/jpeg"
                    style={{ display: "none" }}
                    ref={inputFile}
                    onChange={handleChangeFile}
                  />
                  <div className="profileHero__menu" ref={actionsMenuRef}>
                    <button
                      type="button"
                      className="profileHero__menuTrigger"
                      onClick={() => setShowActionsMenu((v) => !v)}
                      aria-label="More actions"
                      aria-haspopup="menu"
                      aria-expanded={showActionsMenu}
                      title="More actions"
                    >
                      <i className="bi bi-three-dots" aria-hidden="true" />
                    </button>
                    {showActionsMenu && (
                      <div className="profileHero__menuList" role="menu">
                        <button
                          type="button"
                          className="profileHero__menuItem"
                          role="menuitem"
                          onClick={() => {
                            setShowActionsMenu(false);
                            setShowDescriptionModel(true);
                          }}
                        >
                          <i className="bi bi-pencil" aria-hidden="true" />
                          <span>Artist description</span>
                        </button>
                        <button
                          type="button"
                          className="profileHero__menuItem"
                          role="menuitem"
                          onClick={() => {
                            setShowActionsMenu(false);
                            setShowConfirm(true);
                          }}
                        >
                          <i className="bi bi-trash" aria-hidden="true" />
                          <span>Remove photo</span>
                        </button>
                        <button
                          type="button"
                          className="profileHero__menuItem"
                          role="menuitem"
                          onClick={() => {
                            setShowActionsMenu(false);
                            setShowPasswordModal(true);
                          }}
                        >
                          <i className="bi bi-key" aria-hidden="true" />
                          <span>Update password</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {!user ? (
                <Skeleton name="profile-info" loading={true}>
                  <span style={{ display: "block", height: 400 }} />
                </Skeleton>
              ) : !showEditMode ? (
                <>
                  {/* Verification banner */}
                  {!isVerifiedUser && (
                    <div className="formSection profileVerifySection">
                      <div className="formSection__header" style={{ borderBottom: "none", marginBottom: 0, paddingBottom: 0 }}>
                        <div className="formSection__iconWrap">
                          <i
                            className="bi bi-patch-check"
                            aria-hidden="true"
                          />
                        </div>
                        <div className="formSection__titleBlock" style={{ flex: 1 }}>
                          <h3 className="formSection__title">
                            Verify your account
                          </h3>
                          <p className="formSection__subtitle">
                            {verificationStatus &&
                            verificationStatus?.status !==
                              VERIFICATION_REQUEST_STATUS.RESOLVED
                              ? "Your verification request is being reviewed."
                              : isProfileComplete
                              ? "Your profile is complete — submit for verification."
                              : "Complete your profile below to unlock verification."}
                          </p>
                        </div>
                        <div className="profileVerifySection__cta">
                          {verificationStatus &&
                          verificationStatus?.status !==
                            VERIFICATION_REQUEST_STATUS.RESOLVED ? (
                            <VerificationStatusChip
                              status={verificationStatus?.status}
                            />
                          ) : isProfileComplete ? (
                            <button
                              type="button"
                              className="placeBidModal__submit"
                              onClick={() => setShowVerifyProfile(true)}
                            >
                              <i
                                className="bi bi-patch-check"
                                aria-hidden="true"
                              />
                              <span>Get verified</span>
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Personal info */}
                  <div className="formSection">
                    <div className="formSection__header">
                      <div className="formSection__iconWrap">
                        <i className="bi bi-person" aria-hidden="true" />
                      </div>
                      <div className="formSection__titleBlock">
                        <h3 className="formSection__title">Personal info</h3>
                        <p className="formSection__subtitle">
                          Identity and contact details.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="previewEditBtn"
                        onClick={() => setshowEditMode(true)}
                      >
                        <i className="bi bi-pencil" aria-hidden="true" />
                        <span>Edit</span>
                      </button>
                    </div>

                    <DetailRow
                      label="Code"
                      value={
                        user?.code ? (
                          <span className="refCode">{user.code}</span>
                        ) : null
                      }
                    />
                    <DetailRow label="Full name" value={user?.name} />
                    <DetailRow label="Email" value={user?.email} />
                    <DetailRow
                      label="Date of birth"
                      value={user?.dob ? Utils.formatDate(user?.dob) : null}
                    />
                    <DetailRow
                      label="Phone"
                      value={
                        user?.phone ? <PhoneText text={user?.phone} /> : null
                      }
                    />
                    <DetailRow label="Speciality" value={user?.speciality} />
                  </div>

                  {/* Address */}
                  <div className="formSection">
                    <div className="formSection__header">
                      <div className="formSection__iconWrap">
                        <i className="bi bi-geo-alt" aria-hidden="true" />
                      </div>
                      <div className="formSection__titleBlock">
                        <h3 className="formSection__title">Address</h3>
                        <p className="formSection__subtitle">
                          Used for shipping and tax calculations.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="previewEditBtn"
                        onClick={() => setshowEditMode(true)}
                      >
                        <i className="bi bi-pencil" aria-hidden="true" />
                        <span>Edit</span>
                      </button>
                    </div>

                    <DetailRow label="Address line 1" value={user?.address1} />
                    <DetailRow label="Address line 2" value={user?.address2} />
                    <DetailRow label="City" value={user?.city} />
                    <DetailRow label="State" value={user?.state} />
                    <DetailRow label="Zip code" value={user?.zip} />
                  </div>

                  {/* Preferences */}
                  <div className="formSection">
                    <div className="formSection__header">
                      <div className="formSection__iconWrap">
                        <i className="bi bi-sliders" aria-hidden="true" />
                      </div>
                      <div className="formSection__titleBlock">
                        <h3 className="formSection__title">Preferences</h3>
                        <p className="formSection__subtitle">
                          How dates and times are displayed across the app.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="previewEditBtn"
                        onClick={() => setshowEditMode(true)}
                      >
                        <i className="bi bi-pencil" aria-hidden="true" />
                        <span>Edit</span>
                      </button>
                    </div>

                    <DetailRow
                      label="Default timezone"
                      value={user?.timezone}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Edit: identity */}
                  <div className="formSection">
                    <div className="formSection__header">
                      <div className="formSection__iconWrap">
                        <i className="bi bi-person" aria-hidden="true" />
                      </div>
                      <div className="formSection__titleBlock">
                        <h3 className="formSection__title">Personal info</h3>
                        <p className="formSection__subtitle">
                          Tell collectors who they're buying from.
                        </p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <Text
                          name="name"
                          label="Full name"
                          placeholder="John Doe"
                          value={values?.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <Phone
                          name="phone"
                          label="Phone number"
                          value={values?.phone}
                          onChange={handleInputChange}
                          showIcon
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <DoB
                          name="dob"
                          label="Date of birth"
                          placeholder="MM-DD-YYYY"
                          value={values?.dob}
                          onChange={handleInputChange}
                          showIcon
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="field mb-3">
                          <label className="field__label">
                            Speciality
                            <span className="field__required">*</span>
                          </label>
                          <SpecialityDropdown
                            name="speciality"
                            value={values?.speciality}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Edit: address */}
                  <div className="formSection">
                    <div className="formSection__header">
                      <div className="formSection__iconWrap">
                        <i className="bi bi-geo-alt" aria-hidden="true" />
                      </div>
                      <div className="formSection__titleBlock">
                        <h3 className="formSection__title">Address</h3>
                        <p className="formSection__subtitle">
                          Used for shipping calculations and seller
                          verification.
                        </p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <Text
                          name="address1"
                          label="Address line 1"
                          placeholder="Street address or P.O. Box"
                          value={values?.address1}
                          onChange={handleInputChange}
                          icon="bi-house"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <Text
                          name="address2"
                          label="Address line 2"
                          placeholder="Apt, suite, unit, etc."
                          value={values?.address2}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="field mb-3">
                          <label className="field__label">
                            State<span className="field__required">*</span>
                          </label>
                          <StatesDropdown
                            name="state"
                            value={values?.state}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="field mb-3">
                          <label className="field__label">
                            City<span className="field__required">*</span>
                          </label>
                          <CitiesDropdown
                            name="city"
                            value={values?.city}
                            onChange={handleInputChange}
                            stateId={values?.state}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <Zip
                          name="zip"
                          label="Zip code"
                          value={values?.zip}
                          onChange={handleInputChange}
                          showIcon
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Edit: preferences */}
                  <div className="formSection">
                    <div className="formSection__header">
                      <div className="formSection__iconWrap">
                        <i className="bi bi-sliders" aria-hidden="true" />
                      </div>
                      <div className="formSection__titleBlock">
                        <h3 className="formSection__title">Preferences</h3>
                        <p className="formSection__subtitle">
                          Used as the default when creating listings.
                        </p>
                      </div>
                    </div>

                    <Timezone
                      name="timezone"
                      label="Default timezone"
                      value={values?.timezone}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* Sticky action bar */}
                  <div className="formStickyBar">
                    {validationMessage ? (
                      <span className="formStickyBar__msg formStickyBar__msg--error">
                        <i
                          className="bi bi-exclamation-circle"
                          aria-hidden="true"
                        />
                        {validationMessage}
                      </span>
                    ) : (
                      <span className="formStickyBar__msg">
                        Save when you're ready — changes are stored
                        immediately.
                      </span>
                    )}
                    <div className="formStickyBar__actions">
                      <button
                        type="button"
                        className="placeBidModal__cancel"
                        onClick={() => {
                          setshowEditMode(false);
                          setValidationMessage("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="placeBidModal__submit"
                        onClick={handleUpdate}
                      >
                        <i className="bi bi-check2-circle" aria-hidden="true" />
                        <span>Save changes</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <CommonModal
        title="Success"
        message="Verification request is submitted successfully! Admin team will contact you for to proceed it further."
        show={showSuccessPopup}
        onHide={() => setShowSuccessPopup(false)}
      />
      <VerifyProfilePopup
        show={showVerifyProfile}
        onHide={() => setShowVerifyProfile(false)}
        onSuccess={() => {
          refetchVerification();
          setShowVerifyProfile(false);
          setShowSuccessPopup(true);
        }}
      />
      <ArtistDescriptionModal
        show={showDescriptionModel}
        onHide={() => setShowDescriptionModel(false)}
      />
      <LinkPaymentModal
        show={showPaymentModel}
        onHide={() => setShowPaymentModel(false)}
      />
      <UpdatePasswordModal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
      />
      <Confirm
        show={showConfirm}
        onNo={() => setShowConfirm(false)}
        onYes={handleRemovePicture}
        title="Remove picture"
        body="Are you sure you want to remove your profile picture?"
      />
    </>
  );
}

export default Profile;
